import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { FormikContext } from 'formik';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { Icon as MaterialIcon } from '@mui/material';
import useStyles from './FormErrorSummary.styles';
import { useRouter } from 'next/router';
/** Returns the string error for a dot-separated field path from the Formik errors object, or undefined. */
const getNestedError = (errors, path) => {
    const parts = path.split('.');
    let current = errors;
    for (const part of parts) {
        if (!current || typeof current !== 'object')
            return undefined;
        current = current[part];
    }
    if (typeof current === 'string' && current.length > 0) {
        return current;
    }
    if (Array.isArray(current)) {
        const firstMessage = current.find((value) => typeof value === 'string' && value.length > 0);
        return firstMessage;
    }
    return undefined;
};
const getNestedTouched = (touched, path) => {
    const parts = path.split('.');
    let current = touched;
    for (const part of parts) {
        if (!current || typeof current !== 'object')
            return false;
        current = current[part];
    }
    return !!current;
};
const flattenErrorPaths = (value, parentPath = '') => {
    /* istanbul ignore if */
    if (!value || typeof value !== 'object')
        return [];
    return Object.entries(value).flatMap(([key, childValue]) => {
        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        if (typeof childValue === 'string' && childValue.length > 0) {
            return [currentPath];
        }
        return flattenErrorPaths(childValue, currentPath);
    });
};
const FormErrorSummary = ({ fieldNames = [], className, waitForSubmitAfterMount = false, showWhenTouched = false, showAllErrorsWhenTouched = false, }) => {
    var _a, _b, _c;
    const router = useRouter();
    const formikContext = useContext(FormikContext);
    const { classes } = useStyles();
    const containerRef = useRef(null);
    const prevSubmitCount = useRef(0);
    const mountSubmitCount = useRef(null);
    const prevVisible = useRef(false);
    const paths = ['/basket'];
    const pathname = typeof (router === null || router === void 0 ? void 0 : router.pathname) === 'string' ? router.pathname : '';
    const isApplyTopSpacingtoPages = paths.some((path) => pathname.includes(path));
    const errors = (_a = formikContext === null || formikContext === void 0 ? void 0 : formikContext.errors) !== null && _a !== void 0 ? _a : {};
    const touched = (_b = formikContext === null || formikContext === void 0 ? void 0 : formikContext.touched) !== null && _b !== void 0 ? _b : {};
    const submitCount = (_c = formikContext === null || formikContext === void 0 ? void 0 : formikContext.submitCount) !== null && _c !== void 0 ? _c : 0;
    if (mountSubmitCount.current === null) {
        mountSubmitCount.current = submitCount;
        prevSubmitCount.current = submitCount;
    }
    const mountedSubmitCount = mountSubmitCount.current;
    const hasSubmitted = waitForSubmitAfterMount ? submitCount > mountedSubmitCount : submitCount > 0;
    const candidateFields = fieldNames.length > 0 ? fieldNames : flattenErrorPaths(errors);
    const allFieldsWithErrors = candidateFields.filter((name) => getNestedError(errors, name));
    const hasTouchedFieldWithError = allFieldsWithErrors.some((name) => getNestedTouched(touched, name));
    const shouldShow = allFieldsWithErrors.length > 0 && (hasSubmitted || (showWhenTouched && hasTouchedFieldWithError));
    // When triggered by touch only (not a submit), restrict the displayed set to touched fields so
    // untouched fields from other form sections don't inflate the error count.
    const fieldsWithErrors = !hasSubmitted && showWhenTouched && hasTouchedFieldWithError && !showAllErrorsWhenTouched
        ? allFieldsWithErrors.filter((name) => getNestedTouched(touched, name))
        : allFieldsWithErrors;
    const errorCount = fieldsWithErrors.length;
    const firstErrorField = fieldsWithErrors[0];
    useEffect(() => {
        if (submitCount > prevSubmitCount.current && shouldShow && containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        prevSubmitCount.current = submitCount;
    }, [submitCount, shouldShow]);
    useEffect(() => {
        if (shouldShow && !prevVisible.current && containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        prevVisible.current = shouldShow;
    }, [shouldShow]);
    const handleGoToError = useCallback((e) => {
        if (e.type === 'keydown' && e.key !== 'Enter')
            return;
        if (firstErrorField) {
            const el = document.getElementById(firstErrorField);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
            }
        }
    }, [firstErrorField]);
    if (!shouldShow)
        return null;
    const errorWord = errorCount === 1 ? 'error' : 'errors';
    const countWord = errorCount === 1 ? `is ${errorCount}` : `are ${errorCount}`;
    const correctionText = errorCount === 1 ? 'Please correct it and try again.' : 'Please correct them and try again.';
    return (_jsxs("div", { ref: containerRef, className: clsx(classes.container, className, isApplyTopSpacingtoPages && classes.applyMargin), role: "alert", "aria-live": "polite", children: [_jsx(MaterialIcon, { className: classes.icon, children: "warning" }), _jsxs("div", { className: classes.content, children: [_jsx("p", { className: classes.message, children: `There ${countWord} ${errorWord} below.` }), _jsxs("p", { className: classes.subMessage, children: [correctionText, ' ', firstErrorField && (_jsxs("button", { type: "button", className: classes.goToErrorLink, onClick: handleGoToError, onKeyDown: handleGoToError, children: ["Go to ", errorCount > 1 ? 'first' : '', " error"] }))] })] })] }));
};
export default FormErrorSummary;
